import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  CreateNotificationProps,
  NotificationFilter,
  UpdateNotificationProps,
} from 'modules/core/domain/interfaces/supabase/notifications/types';
import supabase from 'modules/core/supabase/supabase';
import { ApiError, toApiError } from 'shared/errors';

/** Postgres/PostgREST: запрошенный диапазон страниц пуст («Requested range not satisfiable»). */
const RANGE_NOT_SATISFIABLE = 'PGRST103';

/**
 * Thunk уведомлений: отказ уходит в Redux как `ApiError` (`rejectWithValue(toApiError(e))`), а не как
 * `Error` — иначе `miniSerializeError` срежет `details` / `hint`, и код Postgres спутается с кодом
 * бэкенда (`.claude/docs/backend-errors.md`).
 *
 * Исключение — пустой диапазон: его пробрасывают как есть, редьюсер узнаёт его по коду и ошибкой не
 * считает (дочитали список до конца).
 */
const createNotificationThunk = <Returned, Arg = void>(type: string, run: (arg: Arg) => Promise<Returned>) =>
  createAsyncThunk<Returned, Arg, { rejectValue: ApiError }>(type, async (arg, { rejectWithValue }) => {
    try {
      return await run(arg);
    } catch (error) {
      if ((error as { code?: string } | null)?.code === RANGE_NOT_SATISFIABLE) throw error;
      return rejectWithValue(toApiError(error));
    }
  });

export const isRangeNotSatisfiable = (error: { code?: string } | undefined) => error?.code === RANGE_NOT_SATISFIABLE;

export const fetchNotifications = createNotificationThunk('notifications/fetchNotifications', async (filter: NotificationFilter) => {
  const { type, sort, limit, offset, userId } = filter;
  const apiFilter: NotificationFilter = {
    limit,
    offset,
    type: type === 'all' ? undefined : type,
    sort: sort === 'asc' ? 'asc' : 'desc',
    userId,
  };
  const { data, count } = await supabase.notifications.getNotifications(apiFilter);
  return { notifications: data, total: count };
});

export const fetchNotificationById = createNotificationThunk('notifications/fetchNotificationById', (id: number) =>
  supabase.notifications.getNotificationById(id),
);

export const createNotification = createNotificationThunk('notifications/createNotification', (props: CreateNotificationProps) =>
  supabase.notifications.createNotification(props),
);

export const updateNotification = createNotificationThunk('notifications/updateNotification', (props: UpdateNotificationProps) =>
  supabase.notifications.updateNotification(props),
);

export const deleteNotification = createNotificationThunk('notifications/deleteNotification', async (id: number) => {
  await supabase.notifications.deleteNotification(id);
  return id;
});
